import { Plus, Sunrise, TriangleAlert, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  type Availability,
  type Booking,
  calendarZone,
  dayRange,
  fitReservations,
  type Interval,
  localInstant,
  quarterHour,
} from '../../shared/calendar';
import { learningQueue } from '../../shared/learning';
import type { LocalRecord, Task } from '../../shared/model';
import type { ReturnSummary } from '../../shared/returning';

import { useWidth } from '../lib/appearance';
import { clockTime, duration, greeting, sinceDay, weekday, workspaceHour } from '../lib/day-words';
import { workspace } from '../lib/engine';
import { goTo, pagePaths } from '../lib/routes';
import { type Mineral, taskStone } from '../lib/stones';
import { taskReason } from './StatusBadge';
import { CairnStack, PebbleMark } from './Stones';
import { TrailScene, useSky } from './TrailScene';
import { Button, Chip, IconButton, Input, QuietButton, Sheet, SheetTitle, type Tone } from './ui';

/** The morning's invitation on Today, once a day until it is taken or put off. */
export function SetOutPrompt({ onOpen, onSkip }: { onOpen: () => void; onSkip: () => void }) {
  return (
    <section className="set-out-prompt">
      <div className="set-out-prompt-text">
        <h2>{greeting(workspaceHour())}.</h2>
        <p>Choose today’s stones, and the hours to carry them.</p>
      </div>
      <CairnStack
        placed={[]}
        planned={['a', 'b', 'd']}
        scale={0.5}
        className="set-out-prompt-cairn"
      />
      <div className="set-out-prompt-actions">
        <Button variant="primary" onClick={onOpen}>
          Set out
        </Button>
        <Button onClick={onSkip}>Not today</Button>
      </div>
    </section>
  );
}

type Block = {
  id: string;
  from: number;
  to: number;
  style: 'appointment' | 'reserved' | 'planned';
  mineral?: Mineral;
};

const hourOf = (time: number, start: number) => (time - start) / 3_600_000;

/**
 * The day's hours as one bar: the windows marked available, what is already
 * booked, and, while Set out is deciding, where each stone would go. The past
 * is dimmed and a hairline marks now. Read as one sentence by assistive
 * technology, because it is one picture.
 */
export function DayBar({
  day,
  windows,
  blocks,
  labels = true,
  summary,
}: {
  day: string;
  windows: Interval[];
  blocks: Block[];
  labels?: boolean;
  summary: string;
}) {
  const [measure, width] = useWidth<HTMLDivElement>();
  const start = Date.parse(dayRange(day).startsAt);
  const spans = windows.map(w => [Date.parse(w.startsAt), Date.parse(w.endsAt)] as const);
  const hours = spans.flatMap(([a, b]) => [hourOf(a, start), hourOf(b, start)]);
  const low = Math.max(0, Math.min(8, Math.floor(Math.min(...hours, 8))));
  const high = Math.min(24, Math.max(20, Math.ceil(Math.max(...hours, 20)), low + 1));
  const span = high - low;
  const x = (time: number) => Math.min(Math.max((hourOf(time, start) - low) / span, 0), 1) * width;
  const now = Date.now();
  const at = x(now);
  const step = span > 14 ? 4 : 2;
  const ticks: number[] = [];

  for (let hour = low + (low % step ? 1 : 0); hour <= high; hour += step) {
    ticks.push(hour);
  }

  return (
    <div
      ref={measure}
      className="day-bar"
      role="img"
      aria-label={summary}
      style={{ height: labels ? 52 : 34 }}
    >
      {width > 0 && (
        <svg width={width} height={labels ? 52 : 34} aria-hidden="true">
          <rect className="day-bar-track" x={0} y={4} width={width} height={26} rx={8} />
          {spans.map(([a, b]) => (
            <rect
              key={a}
              className="day-bar-window"
              x={x(a)}
              y={4}
              width={Math.max(x(b) - x(a), 0)}
              height={26}
              rx={8}
            />
          ))}
          {blocks.map(block => (
            <rect
              key={block.id}
              className={`day-bar-block ${block.style}`}
              data-mineral={block.mineral}
              x={x(block.from) + 2}
              y={9}
              width={Math.max(x(block.to) - x(block.from) - 4, 3)}
              height={16}
              rx={5}
            />
          ))}
          {now > start && at > 0 && (
            <rect className="day-bar-past" x={0} y={4} width={at} height={26} rx={8} />
          )}
          {at > 0 && at < width && (
            <>
              <rect className="day-bar-now" x={at - 0.75} y={1} width={1.5} height={32} />
              <circle className="day-bar-now" cx={at} cy={-2} r={3} />
            </>
          )}
          {labels &&
            ticks.map(hour => (
              <text
                key={hour}
                className="day-bar-label"
                x={((hour - low) / span) * width}
                y={46}
                textAnchor={hour === low ? 'start' : hour === high ? 'end' : 'middle'}
              >
                {hour % 24}
              </text>
            ))}
        </svg>
      )}
    </div>
  );
}

/**
 * One stone to choose or put back. The whole row is the control, and its
 * pebble shows the choice the way a finished task shows on Today.
 */
function ChooseRow({
  record,
  records,
  day,
  chosen,
  marker,
  onToggle,
}: {
  record: LocalRecord;
  records: LocalRecord[];
  day: string;
  chosen: boolean;
  marker: boolean;
  onToggle: () => void;
}) {
  const task = record.value as Task;
  const stone = taskStone(record.id, task);
  const reason = taskReason(task, record.id, records, day);
  const Icon = reason.icon;

  return (
    <button
      type="button"
      data-ui="specialized"
      className="choose-row"
      aria-pressed={chosen}
      aria-label={`${task.title}, ${chosen ? 'chosen for today' : 'not chosen'}. ${reason.label}`}
      onClick={onToggle}
    >
      <span key={String(chosen)} className="choose-pebble">
        <PebbleMark
          mineral={stone.mineral}
          silhouette={stone.silhouette}
          face={chosen ? 'done' : 'open'}
        />
      </span>
      <span className="choose-text">
        <strong>{task.title}</strong>
        <small className={`tone-${reason.tone}`}>
          <Icon aria-hidden="true" />
          {reason.label}
        </small>
        {marker && task.stoppingPoint?.trim() && (
          <em className="marker-quote">“{task.stoppingPoint.trim()}”</em>
        )}
      </span>
      {Boolean(task.estimateMinutes) && (
        <span className={chosen ? 'choose-estimate chosen' : 'choose-estimate'}>
          {duration(task.estimateMinutes!)}
        </span>
      )}
    </button>
  );
}

const nextHour = () => Math.ceil(Date.now() / 3_600_000) * 3_600_000;

const timeInput = (time: number) =>
  new Date(time).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: calendarZone(),
  });

/**
 * Setting out: the morning's choice of stones and the time to carry them, as
 * the phone's Set out sheet. Nothing is written while the owner chooses; Set
 * out writes the day in one go. Closing leaves the day as it was, and Skip
 * leaves it too and stops offering Set out until tomorrow. Windows are the one
 * exception: adding or removing one is a calendar edit made on the spot,
 * because the reservation preview can only be honest about windows that exist.
 */
export function SetOutSheet({
  records,
  name,
  day,
  carried: carriedRecords,
  suggested: suggestedRecords,
  placed,
  returning,
  onClose,
  onFinish,
}: {
  records: LocalRecord[];
  name: string;
  day: string;
  carried: LocalRecord[];
  suggested: LocalRecord[];
  placed: LocalRecord[];
  returning?: ReturnSummary;
  onClose: () => void;
  onFinish: () => void;
}) {
  // The rows are fixed when the sheet opens, so a choice never moves a row
  // out from under the pointer that made it.
  const [carried] = useState(() => carriedRecords.map(r => r.id));
  const [suggested] = useState(() => suggestedRecords.map(r => r.id));
  const [chosen, setChosen] = useState(() => new Set(carried));
  const [reserve, setReserve] = useState(true);
  const [adding, setAdding] = useState<{ from: string; to: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [minute, setMinute] = useState(Date.now());
  const hour = workspaceHour(new Date(minute));
  const sky = useSky(hour);

  useEffect(() => {
    const timer = setInterval(() => setMinute(Date.now()), 60_000);

    return () => clearInterval(timer);
  }, []);

  const byId = new Map(records.filter(r => r.kind === 'task').map(r => [r.id, r]));

  const chosenTasks = [...carried, ...suggested]
    .filter(id => chosen.has(id))
    .map(id => byId.get(id))
    .filter((r): r is LocalRecord => Boolean(r));

  const range = dayRange(day);

  const availability = records.find(
    r =>
      r.kind === 'calendar' &&
      (r.value as Availability).type === 'availability' &&
      (r.value as Availability).day === day,
  );

  const windows = (availability?.value as Availability | undefined)?.windows ?? [];
  const hasWindows = windows.length > 0;

  const bookings = records
    .filter(
      r =>
        r.kind === 'calendar' &&
        (r.value as Booking).type === 'booking' &&
        !(r.value as Booking).cancelled &&
        (r.value as Booking).startsAt < range.endsAt &&
        (r.value as Booking).endsAt > range.startsAt,
    )
    .map(r => ({ id: r.id, value: r.value as Booking }));

  const busyIntervals = bookings.map(b => ({ startsAt: b.value.startsAt, endsAt: b.value.endsAt }));

  // Free window time from now on, around what is already booked.
  const freeMinutes = windows.reduce((sum, window) => {
    let cursor = Math.max(Date.parse(window.startsAt), minute);
    const end = Date.parse(window.endsAt);
    let free = 0;

    for (const booking of [...busyIntervals].sort((a, b) => a.startsAt.localeCompare(b.startsAt))) {
      const from = Date.parse(booking.startsAt),
        to = Date.parse(booking.endsAt);

      if (to <= cursor || from >= end) {
        continue;
      }

      free += Math.max(0, from - cursor);
      cursor = Math.max(cursor, to);
    }

    return sum + free + Math.max(0, end - cursor);
  }, 0);

  const chosenIds = new Set(chosenTasks.map(r => r.id));

  const reservedForChosen = bookings
    .filter(b => b.value.taskId && chosenIds.has(b.value.taskId))
    .reduce((sum, b) => sum + Date.parse(b.value.endsAt) - Date.parse(b.value.startsAt), 0);

  const openMinutes = Math.round((freeMinutes + reservedForChosen) / 60_000);

  const chosenMinutes = chosenTasks.reduce(
    (sum, r) => sum + ((r.value as Task).estimateMinutes ?? 0),
    0,
  );

  const alreadyReserved = new Set(bookings.map(b => b.value.taskId).filter(Boolean));

  const placements =
    reserve && hasWindows
      ? fitReservations(
          chosenTasks
            .filter(r => !alreadyReserved.has(r.id))
            .map(r => ({
              taskId: r.id,
              minutes: (r.value as Task).estimateMinutes || 30,
            })),
          windows,
          busyIntervals,
          quarterHour(minute),
        )
      : [];

  const unplaced = placements
    .filter(p => !p.slot)
    .map(p => (byId.get(p.taskId)?.value as Task | undefined)?.title)
    .filter(Boolean);

  const bookedBlocks: Block[] = bookings.map(({ id, value }) => {
    const task = value.taskId ? byId.get(value.taskId) : undefined;

    return {
      id,
      from: Date.parse(value.startsAt),
      to: Date.parse(value.endsAt),
      style: task ? 'reserved' : 'appointment',
      mineral: task ? taskStone(task.id, task.value as Task).mineral : undefined,
    };
  });

  const plannedBlocks: Block[] = placements.flatMap(p => {
    const task = byId.get(p.taskId);

    return p.slot && task
      ? [
          {
            id: `plan-${p.taskId}`,
            from: Date.parse(p.slot.startsAt),
            to: Date.parse(p.slot.endsAt),
            style: 'planned' as const,
            mineral: taskStone(task.id, task.value as Task).mineral,
          },
        ]
      : [];
  });

  const rangeWords = (w: Interval) => `${clockTime(w.startsAt)} – ${clockTime(w.endsAt)}`;

  const openLabel = !hasWindows
    ? 'No hours set'
    : openMinutes > 0
      ? `${duration(openMinutes)} open`
      : 'All booked';

  const windowsSummary = hasWindows
    ? `Available ${windows.map(rangeWords).join(', ').replaceAll(' – ', ' to ')}. ${openLabel}.`
    : 'No hours are marked available today.';

  const plannedSummary = plannedBlocks.length
    ? `Would reserve: ${placements
        .filter(p => p.slot)
        .map(
          p => `${(byId.get(p.taskId)?.value as Task | undefined)?.title}, ${rangeWords(p.slot!)}`,
        )
        .join('; ')}.`
    : 'Nothing new to reserve.';

  const count = chosenTasks.length;

  const countLine =
    count === 0
      ? 'No stones yet'
      : `${count} ${count === 1 ? 'stone' : 'stones'}${chosenMinutes ? ` · about ${duration(chosenMinutes)}` : ''}`;

  // The day's load in words. Never a limit: a sixth stone can always be chosen.
  const load: { text: string; tone?: Tone } = (() => {
    if (count === 0) {
      return { text: 'Choose one or two to begin.' };
    }

    if (!hasWindows || openMinutes <= 0) {
      return count <= 3
        ? { text: 'A manageable day.', tone: 'success' }
        : count <= 5
          ? { text: 'A full day. Consider moving something.', tone: 'attention' }
          : { text: 'More than a day holds. Something will carry over.', tone: 'danger' };
    }

    const open = duration(openMinutes);

    if (chosenMinutes > openMinutes) {
      return { text: `More than the ${open} open. Something will carry over.`, tone: 'danger' };
    }

    return count <= 3
      ? { text: `Of ${open} open. A good day.`, tone: 'success' }
      : count <= 5
        ? { text: `Of ${open} open. A full day.`, tone: 'attention' }
        : { text: `Of ${open} open. More than a day holds.`, tone: 'danger' };
  })();

  const learning = learningQueue(records, minute).queue.length;
  const first = name.split(' ')[0] ?? '';

  async function saveWindows(next: Interval[]) {
    setError('');

    try {
      await workspace.save(availability?.id ?? crypto.randomUUID(), 'calendar', {
        type: 'availability',
        day,
        zone: calendarZone(),
        windows: next,
      });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function addWindow() {
    if (!adding) {
      return;
    }

    try {
      const startsAt = localInstant(`${day}T${adding.from}`),
        endsAt = localInstant(`${day}T${adding.to}`);

      if (endsAt <= startsAt) {
        throw new Error('The end has to be after the start.');
      }

      await saveWindows(
        [...windows, { startsAt, endsAt }].sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
      );

      setAdding(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function setOut(thenLearning = false) {
    if (busy) {
      return;
    }

    setBusy(true);
    setError('');

    try {
      const values = new Map<string, Task>();

      for (const id of [...carried, ...suggested]) {
        const record = byId.get(id);

        if (record && Boolean((record.value as Task).today) !== chosen.has(id)) {
          values.set(id, { ...(record.value as Task), today: chosen.has(id) });
        }
      }

      const changes: { id: string; kind: 'task' | 'calendar'; value: Task | Booking }[] = [];

      for (const placement of placements) {
        const record = byId.get(placement.taskId);

        if (!placement.slot || !record) {
          continue;
        }

        const task = values.get(record.id) ?? (record.value as Task);

        const minutes = Math.round(
          (Date.parse(placement.slot.endsAt) - Date.parse(placement.slot.startsAt)) / 60_000,
        );

        changes.push({
          id: crypto.randomUUID(),
          kind: 'calendar',
          value: {
            type: 'booking',
            zone: calendarZone(),
            personId: null,
            followUpTaskIds: [],
            title: task.title,
            startsAt: placement.slot.startsAt,
            endsAt: placement.slot.endsAt,
            taskId: record.id,
            noteId: null,
            notes: '',
            reminderMinutes: 15,
            cancelled: false,
            history: [],
          },
        });

        // The task's side of reserving time, as the calendar writes it.
        values.set(record.id, { ...task, estimateMinutes: minutes, today: true });
      }

      for (const [id, value] of values) {
        changes.push({ id, kind: 'task', value });
      }

      if (changes.length) {
        await workspace.saveMany(changes);
      }

      onFinish();
      onClose();

      if (thenLearning) {
        goTo(pagePaths.Learning);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function toggle(id: string) {
    setChosen(current => {
      const next = new Set(current);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  const rows = (ids: string[], markers: boolean) => (
    <div className="choose-list">
      {ids
        .map(id => byId.get(id))
        .filter((r): r is LocalRecord => Boolean(r))
        .map(record => (
          <ChooseRow
            key={record.id}
            record={record}
            records={records}
            day={day}
            chosen={chosen.has(record.id)}
            marker={markers}
            onToggle={() => toggle(record.id)}
          />
        ))}
    </div>
  );

  return (
    <Sheet onClose={onClose} busy={busy} className="set-out">
      <div className="set-out-scroll">
        <header className="set-out-header">
          <TrailScene sky={sky} height={262}>
            <CairnStack
              placed={placed.map(r => taskStone(r.id, r.value as Task))}
              held={chosenTasks.map(r => taskStone(r.id, r.value as Task))}
              planned={chosenTasks.length || placed.length ? [] : ['a']}
            />
          </TrailScene>
          <div className="set-out-titles">
            <span>
              {weekday(day)} · {clockTime(minute)}
            </span>
            <SheetTitle>
              {greeting(hour)}
              {first ? (
                <>
                  ,<br />
                  {first}.
                </>
              ) : (
                '.'
              )}
            </SheetTitle>
            <p>Choose today’s stones.</p>
          </div>
          <div className="set-out-controls">
            <IconButton
              className="glass-control"
              label="Close, leaving today as it was"
              title="Close"
              disabled={busy}
              onClick={onClose}
            >
              <X aria-hidden="true" />
            </IconButton>
            <QuietButton
              className="glass-control"
              disabled={busy}
              title="Leaves today as it is and doesn’t offer Set out again until tomorrow"
              onClick={() => {
                onFinish();
                onClose();
              }}
            >
              Skip
            </QuietButton>
          </div>
        </header>
        <div className="set-out-body">
          {error && (
            <p className="error-message" role="alert">
              {error}
            </p>
          )}
          <section>
            <div className="section-heading today-section-heading">
              <h2>How much trail today?</h2>
              <span className={hasWindows ? 'set-out-open has-windows' : 'set-out-open'}>
                {openLabel}
              </span>
            </div>
            <div className="detail-card set-out-card">
              <DayBar day={day} windows={windows} blocks={bookedBlocks} summary={windowsSummary} />
              <div className="set-out-chips">
                {windows.map(window => (
                  <Chip
                    key={window.startsAt}
                    tone="success"
                    aria-label={`Remove the window ${rangeWords(window)}`}
                    onClick={() => void saveWindows(windows.filter(w => w !== window))}
                  >
                    {rangeWords(window)}
                    <X aria-hidden="true" />
                  </Chip>
                ))}
                <Chip
                  onClick={() => {
                    const from = Math.min(nextHour(), Date.parse(range.endsAt) - 3_600_000);
                    const to = Math.min(from + 3 * 3_600_000, Date.parse(range.endsAt) - 60_000);

                    setAdding({ from: timeInput(from), to: timeInput(to) });
                  }}
                >
                  <Plus aria-hidden="true" />
                  Add a window
                </Chip>
              </div>
              {adding && (
                <div className="set-out-add-window">
                  <label>
                    From
                    <Input
                      type="time"
                      value={adding.from}
                      onChange={e => setAdding({ ...adding, from: e.target.value })}
                    />
                  </label>
                  <label>
                    Until
                    <Input
                      type="time"
                      value={adding.to}
                      onChange={e => setAdding({ ...adding, to: e.target.value })}
                    />
                  </label>
                  <div className="button-row">
                    <Button variant="primary" onClick={() => void addWindow()}>
                      Add
                    </Button>
                    <Button onClick={() => setAdding(null)}>Cancel</Button>
                  </div>
                  <p className="small-muted">
                    The hours you mean to work today. Windows can’t overlap.
                  </p>
                </div>
              )}
              {!hasWindows && !adding && (
                <p className="small-muted">
                  Mark the hours you mean to work. Set out can then reserve a place in them for each
                  stone.
                </p>
              )}
            </div>
          </section>
          {carried.length > 0 && (
            <section>
              <div className="section-heading today-section-heading">
                <h2>
                  {returning
                    ? `Carried from ${sinceDay(returning.lastDay, day)}`
                    : 'Already on today'}
                </h2>
                <span className="muted">Still yours</span>
              </div>
              <div className="detail-card set-out-card">{rows(carried, true)}</div>
              <p className="set-out-footnote">
                Unfinished stones stay on today until you finish them or move them. Untick one to
                move it back to the Inbox.
              </p>
            </section>
          )}
          <section>
            <div className="section-heading today-section-heading">
              <h2>Suggested for today</h2>
              {suggested.length > 0 && <span className="muted">Each says why</span>}
            </div>
            {suggested.length ? (
              <div className="detail-card set-out-card">{rows(suggested, false)}</div>
            ) : (
              <div className="empty-state compact">
                <Sunrise aria-hidden="true" />
                <div>
                  <h2>Nothing else is waiting</h2>
                  <p>Capture on Today when something comes up.</p>
                </div>
              </div>
            )}
          </section>
          <section className="detail-card set-out-card set-out-reserve">
            <label className="set-out-toggle">
              <Input
                type="checkbox"
                role="switch"
                checked={reserve && hasWindows}
                disabled={!hasWindows}
                onChange={e => setReserve(e.target.checked)}
              />
              <span>
                <strong>Reserve time in my windows</strong>
                <small>
                  {hasWindows
                    ? 'Each stone gets a place in the calendar. You can still move it.'
                    : 'Add a window above, and each stone can have a place in it.'}
                </small>
              </span>
            </label>
            {reserve && hasWindows && chosenTasks.length > 0 && (
              <>
                <DayBar
                  day={day}
                  windows={windows}
                  blocks={[...bookedBlocks, ...plannedBlocks]}
                  labels={false}
                  summary={plannedSummary}
                />
                {unplaced.length > 0 && (
                  <p className="set-out-unplaced">
                    <TriangleAlert aria-hidden="true" />
                    No room left for {unplaced.map(t => `“${t}”`).join(' and ')}.
                  </p>
                )}
              </>
            )}
          </section>
        </div>
      </div>
      <footer className="set-out-footer">
        <div className="set-out-summary">
          <CairnStack
            placed={[]}
            held={chosenTasks.slice(0, 6).map(r => taskStone(r.id, r.value as Task))}
            planned={chosenTasks.length ? [] : ['a']}
            scale={0.44}
            className="set-out-footer-cairn"
          />
          <span>
            <strong>{countLine}</strong>
            <small className={load.tone ? `tone-${load.tone}` : undefined}>{load.text}</small>
          </span>
        </div>
        <Button variant="primary" disabled={busy} onClick={() => void setOut()}>
          {busy ? 'Setting out…' : 'Set out →'}
        </Button>
        {learning > 0 && (
          <QuietButton
            className="set-out-learning"
            disabled={busy}
            onClick={() => void setOut(true)}
          >
            Then Learning · {learning} {learning === 1 ? 'card' : 'cards'}
          </QuietButton>
        )}
      </footer>
    </Sheet>
  );
}
